import { useEffect } from 'react';

/**
 * Production site URL — used for canonical/OG URLs across all pages.
 * If you ever change the public domain, update this single constant.
 */
export const SITE_URL = 'https://agencive.fi';

/**
 * Sets document.title, meta description, OG/Twitter tags and a canonical
 * <link rel="canonical"> for the current route. Restores previous values
 * on unmount so each page declares its own metadata cleanly.
 */
export const usePageMeta = ({ title, description, ogTitle, ogDescription, path, jsonLd }) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, content) => {
      if (!content) return null;
      const el = document.querySelector(selector);
      if (!el) return null;
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return () => { if (prev != null) el.setAttribute('content', prev); };
    };

    // Canonical: <link rel="canonical" href="...">. Create if missing.
    const canonicalHref = (() => {
      const p = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
      // Normalize trailing slash for root; otherwise no trailing slash.
      const clean = p === '/' ? '/' : p.replace(/\/$/, '');
      return `${SITE_URL}${clean}`;
    })();

    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const prevCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', canonicalHref);

    // og:url should also follow canonical
    const restoreOgUrl = setMeta('meta[property="og:url"]', canonicalHref);

    const restorers = [
      setMeta('meta[name="description"]', description),
      setMeta('meta[property="og:title"]', ogTitle || title),
      setMeta('meta[property="og:description"]', ogDescription || description),
      setMeta('meta[name="twitter:title"]', ogTitle || title),
      setMeta('meta[name="twitter:description"]', ogDescription || description),
      restoreOgUrl,
    ].filter(Boolean);

    // JSON-LD structured data: inject and clean up per route
    const injectedScripts = [];
    if (jsonLd && Array.isArray(jsonLd)) {
      jsonLd.forEach((obj) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.pageJsonLd = 'true';
        script.textContent = JSON.stringify(obj);
        document.head.appendChild(script);
        injectedScripts.push(script);
      });
    }

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r && r());
      if (createdCanonical && canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      } else if (prevCanonical != null) {
        canonical.setAttribute('href', prevCanonical);
      }
      injectedScripts.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); });
    };
  }, [title, description, ogTitle, ogDescription, path, jsonLd]);
};

export default usePageMeta;
